import {AGENCY_KEYS} from "@/api/agencyApi";
import {COMPANY_KEYS, useUpdateCreditsCache} from "@/api/companyApi";
import {PAYMENT_KEYS} from "@/api/paymentApi";
import type {BuyoutBundleSerializer} from "@/generated/DjangoTypes";
import {useFetchUser} from "@/hooks/useFetchUser";
import usePackageLimits from "@/hooks/usePackageLimits";
import {useGenericQuery} from "@/hooks/useQueryHooks";
import {client} from "@/httpClient";
import {capturePostHogEvent, PostHogEvents} from "@/posthog";
import {queryClient} from "@/queryClient";
import {useMutation} from "@tanstack/react-query";
import {useState} from "react";

export type TBuyoutBundleType = BuyoutBundleSerializer["bundle_type"];

export type TContentBundle = {
  active: boolean;
  buyout: number;
  campaigns: number;
  created: string;
  id: number;
  is_most_popular: boolean;
  modified: string;
  name: string;
  prices: {
    currency: string;
    id: number;
    price: number;
    stripe_price_id: string;
  }[];
  seats: number;
  stripe_product_id: string;
  stripe_tax_id: string;
  vat?: number;
};

const useBuyoutBundlePurchase = ({
  agencyId,
  companyId,
  credits: scopeCredits,
  currency: scopeCurrency,
  onSuccess,
  type,
}: {
  agencyId?: number;
  companyId?: number;
  credits?: number;
  currency?: string;
  onSuccess?(): void;
  type: TBuyoutBundleType;
}) => {
  const packageLimits = usePackageLimits();
  const credits = scopeCredits ?? packageLimits.credits;
  const currency = scopeCurrency ?? packageLimits.currency;
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const {refetch} = useFetchUser();
  const updateCreditsCache = useUpdateCreditsCache(agencyId || companyId ? {agencyId, companyId} : undefined);

  const {data: bundlesData, isLoading} = useGenericQuery<TContentBundle[]>({
    queryKey: PAYMENT_KEYS.qBuyoutBundleList(`currency=${currency}&bundle_type=${type}`),
    enabled: !!currency,
  });
  const bundles = (bundlesData ?? []).filter(bundle => !!bundle.prices.length);
  const selectedBundle = selectedOption !== null ? bundles[selectedOption] : null;
  const selectedBundlePrice = !!selectedBundle
    ? +(selectedBundle.prices[0].price * (1 + (selectedBundle.vat ?? 0) / 100)).toFixed(2)
    : 0;
  const creditsToApply = Math.min(credits, selectedBundlePrice);
  const amountToPay = Math.max(0, selectedBundlePrice - creditsToApply);
  const creditsToRemain = Math.round(Math.max(0, credits - creditsToApply) * 100) / 100;

  const {mutateAsync, isPending} = useMutation({
    mutationKey: PAYMENT_KEYS.mBuyoutBundleOneTimePayment,
    mutationFn: ({price_id}: {price_id: number}) =>
      client.post(PAYMENT_KEYS.mBuyoutBundleOneTimePayment[0], {
        price_id,
        success_url: window.location.href,
        cancel_url: window.location.href,
      }),
    onSuccess: response => {
      capturePostHogEvent(PostHogEvents.BundlePurchased, {
        bundleType: type,
        bundleQuantity: selectedBundle?.buyout ?? selectedBundle?.seats ?? selectedBundle?.campaigns,
        creditsApplied: creditsToApply,
        paidWithStripe: !!response.data.stripe_url,
        price: selectedBundlePrice,
      });
      if (response.data.stripe_url) {
        window.location.href = response.data.stripe_url;
      } else {
        onSuccess?.();

        setTimeout(() => {
          if (!!creditsToApply) {
            updateCreditsCache(-creditsToApply);
          }

          refetch();

          if (agencyId) {
            queryClient.invalidateQueries({queryKey: AGENCY_KEYS.qAgency(agencyId)});
          }

          if (companyId) {
            queryClient.invalidateQueries({queryKey: COMPANY_KEYS.qCompany(companyId)});
          }
        }, 2000);
      }
    },
  });

  return {
    amountToPay,
    bundles,
    credits,
    creditsToApply,
    creditsToRemain,
    currency,
    isLoading,
    isPending,
    purchase: async () => await mutateAsync({price_id: bundles[selectedOption!].prices[0].id}),
    selectedBundlePrice,
    selectedOption,
    setSelectedOption,
  };
};

export type TBuyoutBundlePurchase = ReturnType<typeof useBuyoutBundlePurchase>;

export default useBuyoutBundlePurchase;
